"use client"

import React, { createContext, useContext, ReactNode, useState, useCallback, useMemo, useEffect } from 'react'
import { ENGAGEMENT_TABS, type EngagementTab } from '@/types/ui/engagement-view'

// Navigation context interface
interface EngagementNavigationContextType {
  activeTab: EngagementTab
  activeSection: string | null
  sections: Array<{
    id: string
    label: string
    icon: React.ReactNode
  }>
  viewedSections: Set<string>
  goToSection: (sectionId: string) => void
  unmarkSectionViewed: (sectionId: string) => void
  goToTab: (tabId: EngagementTab) => void
}

interface EngagementNavigationProviderProps {
  children: ReactNode
  sections: Array<{
    id: string
    label: string
    icon: React.ReactNode
  }>
  engagementKey: string
}

const EngagementNavigationContext = createContext<EngagementNavigationContextType | undefined>(undefined)

export function EngagementNavigationProvider({ 
  children, 
  sections,
  engagementKey
}: EngagementNavigationProviderProps) {
  // Helper to get initial viewed sections (always just the first section)
  const getInitialViewedSections = useCallback(() => {
    const firstSection = sections[0]?.id
    return new Set(firstSection ? [firstSection] : [])
  }, [sections])

  const [activeTab, setActiveTab] = useState<EngagementTab>(ENGAGEMENT_TABS.DETAILS)
  const [activeSection, setActiveSection] = useState<string | null>(() =>
    sections.length > 0 ? sections[0].id : null
  )
  const [viewedSections, setViewedSections] = useState<Set<string>>(getInitialViewedSections)

  // Reset navigation state when engagement changes
  useEffect(() => {
    // Reset viewed sections
    setViewedSections(getInitialViewedSections())
  }, [engagementKey, getInitialViewedSections, sections])

  // Navigate to a specific tab
  const goToTab = useCallback((tabId: EngagementTab) => {
    setActiveTab(tabId)
  }, [])

  const unmarkSectionViewed = useCallback((sectionId: string) => {
    setViewedSections(prev => {
      const next = new Set(prev)
      next.delete(sectionId)
      return next
    })
  }, [])

  // Navigate to a specific form section
  const goToSection = useCallback((sectionId: string) => {
    // Switch to details tab if needed
    if (activeTab !== ENGAGEMENT_TABS.DETAILS) {
      setActiveTab(ENGAGEMENT_TABS.DETAILS)
    }
    // Set active section directly (no scrolling)
    setActiveSection(sectionId)
    // Mark section as viewed
    setViewedSections(prev => new Set(prev).add(sectionId))
  }, [activeTab])

  // Stable context value
  const stableContextValue: EngagementNavigationContextType = useMemo(() => ({
    activeTab,
    activeSection,
    sections,
    viewedSections,
    goToSection,
    unmarkSectionViewed,
    goToTab,
  }), [activeTab, activeSection, sections, viewedSections, goToSection, unmarkSectionViewed, goToTab])

  return (
    <EngagementNavigationContext.Provider value={stableContextValue}>
      {children}
    </EngagementNavigationContext.Provider>
  )
}

// Hook for navigation (includes activeSection)
export function useEngagementNavigation(): EngagementNavigationContextType {
  const context = useContext(EngagementNavigationContext)
  if (context === undefined) {
    throw new Error('useEngagementNavigation must be used within an EngagementNavigationProvider')
  }
  return context
} 